// ScheduledNotificationService — migrated to the OpenAPI-generated client.
//
// Preserves each function's pre-migration return shape (getters returned the raw axios
// `response`; callers read `res.data` / `res.data.content`), so we return `{data}` with the
// parsed JSON body. The SDK builds the query string (no hand-rolled URLSearchParams) and
// auth/timezone come from configureApi's interceptor.
//
// The search endpoint (/admin/scheduled-notification/search) uses FLAT page/size params, not a
// nested Spring Pageable, so it is safe to migrate.
import {
    adminScheduledNotificationControllerSearch,
    adminScheduledNotificationControllerGetById,
    adminScheduledNotificationControllerCreate,
    adminScheduledNotificationControllerUpdate,
    adminScheduledNotificationControllerDelete,
    adminScheduledNotificationControllerActivate,
    adminScheduledNotificationControllerDeactivate,
} from '../api/generated';

const throwAsAxiosError = (error, response) => {
    throw {response: {data: error, status: response?.status}};
};

export const getScheduledNotifications = async (pageId = 0, pageSize = 20, keyword, companyId, isActive, trainerId) => {
    const {data, error, response} = await adminScheduledNotificationControllerSearch({
        query: {
            page: pageId,
            size: pageSize,
            ...(keyword ? {keyword} : {}),
            ...(companyId !== null && companyId !== undefined ? {companyId} : {}),
            ...(isActive !== null && isActive !== undefined ? {isActive} : {}),
            ...(trainerId !== null && trainerId !== undefined && trainerId !== 0 ? {trainerId} : {}),
        },
    });
    if (error) throwAsAxiosError(error, response);
    return {data};
};

export const getScheduledNotificationById = async (id) => {
    const {data, error, response} = await adminScheduledNotificationControllerGetById({
        path: {id},
    });
    if (error) throwAsAxiosError(error, response);
    return {data};
};

export const saveScheduledNotification = async (notificationData) => {
    const requestData = {
        title: notificationData.title,
        body: notificationData.body,
        scope: notificationData.scope,
        audienceType: notificationData.audienceType,
        scheduleDays: notificationData.scheduleDays,
        scheduleTime: notificationData.scheduleTime,
        isActive: notificationData.isActive ?? true,
    };

    // Include companyId for SINGLE_COMPANY and TRAINER scopes
    if (notificationData.companyId) {
        requestData.companyId = notificationData.companyId;
    }

    // Include trainerId for TRAINER scope
    if (notificationData.trainerId) {
        requestData.trainerId = notificationData.trainerId;
    }

    // Include targetCompanyIds for MULTI_COMPANY scope
    if (notificationData.targetCompanyIds && notificationData.targetCompanyIds.length > 0) {
        requestData.targetCompanyIds = notificationData.targetCompanyIds;
    }

    // Include targetUserIds for SPECIFIC_USERS audience type
    if (notificationData.targetUserIds && notificationData.targetUserIds.length > 0) {
        requestData.targetUserIds = notificationData.targetUserIds;
    }

    const {data, error, response} = await adminScheduledNotificationControllerCreate({
        body: requestData,
    });
    if (error) throwAsAxiosError(error, response);
    return {data};
};

export const updateScheduledNotification = async (id, notificationData) => {
    const requestData = {};

    if (notificationData.title !== undefined) {
        requestData.title = notificationData.title;
    }
    if (notificationData.body !== undefined) {
        requestData.body = notificationData.body;
    }
    if (notificationData.audienceType !== undefined) {
        requestData.audienceType = notificationData.audienceType;
    }
    if (notificationData.scheduleDays !== undefined) {
        requestData.scheduleDays = notificationData.scheduleDays;
    }
    if (notificationData.scheduleTime !== undefined) {
        requestData.scheduleTime = notificationData.scheduleTime;
    }
    if (notificationData.isActive !== undefined) {
        requestData.isActive = notificationData.isActive;
    }
    if (notificationData.targetUserIds !== undefined) {
        requestData.targetUserIds = notificationData.targetUserIds;
    }

    const {data, error, response} = await adminScheduledNotificationControllerUpdate({
        path: {id},
        body: requestData,
    });
    if (error) throwAsAxiosError(error, response);
    return {data};
};

export const deleteScheduledNotification = async (id) => {
    const {data, error, response} = await adminScheduledNotificationControllerDelete({
        path: {id},
    });
    if (error) throwAsAxiosError(error, response);
    return {data};
};

export const toggleScheduledNotificationStatus = async (id, isActive) => {
    const call = isActive
        ? adminScheduledNotificationControllerActivate
        : adminScheduledNotificationControllerDeactivate;

    const {data, error, response} = await call({
        path: {id},
    });
    if (error) throwAsAxiosError(error, response);
    return {data};
};

// Notification scope enum values
export const NOTIFICATION_SCOPES = {
    GLOBAL: 'GLOBAL',
    MULTI_COMPANY: 'MULTI_COMPANY',
    SINGLE_COMPANY: 'SINGLE_COMPANY',
    TRAINER: 'TRAINER'
};

// Scope labels in Hebrew
export const SCOPE_LABELS = {
    GLOBAL: 'כל החברות',
    MULTI_COMPANY: 'חברות נבחרות',
    SINGLE_COMPANY: 'חברה בודדת',
    TRAINER: 'מתאמני מאמן'
};

// Audience type enum values
export const AUDIENCE_TYPES = {
    ALL_USERS: 'ALL_USERS',
    SPECIFIC_USERS: 'SPECIFIC_USERS'
};

// Days of week for scheduling
export const DAYS_OF_WEEK = [
    { value: 'SUNDAY', label: 'ראשון', shortLabel: 'א' },
    { value: 'MONDAY', label: 'שני', shortLabel: 'ב' },
    { value: 'TUESDAY', label: 'שלישי', shortLabel: 'ג' },
    { value: 'WEDNESDAY', label: 'רביעי', shortLabel: 'ד' },
    { value: 'THURSDAY', label: 'חמישי', shortLabel: 'ה' },
    { value: 'FRIDAY', label: 'שישי', shortLabel: 'ו' },
    { value: 'SATURDAY', label: 'שבת', shortLabel: 'ש' }
];
